import { useState, useCallback } from "react";
import { Check, Image as ImageIcon, Package } from "react-feather";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Product } from "@/types/campaign";

interface ProductImageSelectorProps {
  products: Product[];
  selectedProductIds: string[];
  onChange: (productIds: string[]) => void;
  maxSelections?: number;
}

export function ProductImageSelector({
  products,
  selectedProductIds,
  onChange,
  maxSelections = 4,
}: ProductImageSelectorProps) {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredProducts = products.filter((product) =>
    product.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleProductToggle = useCallback(
    (productId: string) => {
      if (selectedProductIds.includes(productId)) {
        // Remove product
        onChange(selectedProductIds.filter((id) => id !== productId));
      } else {
        // Add product if under limit
        if (selectedProductIds.length < maxSelections) {
          onChange([...selectedProductIds, productId]);
        }
      }
    },
    [selectedProductIds, onChange, maxSelections]
  );

  const isSelected = (productId: string) =>
    selectedProductIds.includes(productId);

  return (
    <Card className="max-h-[650px] overflow-y-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="w-5 h-5" />
          Product Showcase Images
        </CardTitle>
        <CardDescription>
          Select up to {maxSelections} product images to showcase on your brand
          details page. These will be displayed as a carousel alongside your
          other showcase images.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
        </div>

        {/* Products Grid */}
        {filteredProducts.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {filteredProducts.map((product) => (
              <div
                key={product.image}
                className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                  isSelected(product.image)
                    ? "border-brand-500 ring-2 ring-brand-200"
                    : "border-border hover:border-border"
                }`}
                onClick={() => handleProductToggle(product.image)}
              >
                {/* Product Image */}
                <div className="aspect-square relative">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "/placeholder/no-image.jpg";
                    }}
                  />

                  {/* Selection Overlay */}
                  {isSelected(product.image) && (
                    <div className="absolute inset-0 bg-brand-500 bg-opacity-20 flex items-center justify-center">
                      <div className="w-8 h-8 bg-brand-500 rounded-full flex items-center justify-center">
                        <Check className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  )}

                  {/* Selection Number */}
                  {isSelected(product.image) && (
                    <div className="absolute top-2 left-2 bg-brand-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">
                      {selectedProductIds.indexOf(product.image) + 1}
                    </div>
                  )}
                </div>

                {/* Product Info */}
                <div className="p-2 bg-card">
                  <h4 className="text-sm font-medium text-foreground truncate">
                    {product.title}
                  </h4>
                  <p className="text-xs text-muted-foreground">${product.price}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <ImageIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>
              {searchTerm
                ? `No products found matching "${searchTerm}"`
                : "No products available"}
            </p>
          </div>
        )}

        {/* Selection Count */}
        <div className="text-sm text-muted-foreground text-center">
          {selectedProductIds.length} of {maxSelections} products selected
        </div>

        {/* Selected Products Summary */}
        {selectedProductIds.length > 0 && (
          <div className="mt-4 p-3 bg-brand-50 rounded-lg">
            <h4 className="text-sm font-medium text-brand-900 mb-2">
              Selected Products:
            </h4>
            <div className="flex flex-wrap gap-2">
              {selectedProductIds.map((productId, index) => {
                const product = products.find((p) => p.image === productId);
                return (
                  <div
                    key={productId}
                    className="flex items-center gap-2 bg-card px-2 py-1 rounded border text-xs"
                  >
                    <span className="w-4 h-4 bg-brand-500 text-white rounded-full flex items-center justify-center text-xs">
                      {index + 1}
                    </span>
                    <span className="truncate max-w-32">
                      {product?.title || "Unknown Product"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
